import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  Building2, 
  Users, 
  CreditCard, 
  FileText, 
  BarChart3, 
  Settings, 
  Wrench,
  Upload,
  LogOut,
  X,
  Calendar,
  Shield
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';

const Sidebar = ({ userRole = 'landlord', onClose }) => {
  const { logout, user } = useAuth();
  const location = useLocation();
  
  // Check if current user is admin
  const isAdmin = user?.is_admin === true;
  
  // Detect if we're in org-scoped tenant routes
  const isOrgScoped = location.pathname.includes('/o/');
  const orgMatch = location.pathname.match(/\/o\/([^\/]+)/);
  const orgId = orgMatch ? orgMatch[1] : null;
  const tenantBasePath = isOrgScoped && orgId ? `/o/${orgId}/tenant` : '/tenant';

  const landlordMenuItems = [
    { icon: Home, label: 'Dashboard', path: '/owner/dashboard' },
    { icon: Building2, label: 'Properties', path: '/owner/properties' },
    { icon: Users, label: 'Tenants', path: '/owner/tenants' },
    { icon: CreditCard, label: 'Payments', path: '/owner/payments' },
    { icon: FileText, label: 'Leases', path: '/owner/leases' },
    { icon: Wrench, label: 'Work Orders', path: '/owner/maintenance' },
    { icon: BarChart3, label: 'Reports', path: '/owner/reports' },
    { icon: Upload, label: 'Documents', path: '/owner/documents' },
    { icon: Settings, label: 'Settings', path: '/owner/settings' }
  ];
  
  // Add Admin link for admin users only
  if (isAdmin) {
    landlordMenuItems.push({ 
      icon: Shield, 
      label: 'Admin Panel', 
      path: '/admin',
      isAdmin: true // Mark this as admin-only
    });
  }

  const tenantMenuItems = [
    { icon: Home, label: 'Dashboard', path: `${tenantBasePath}` },
    { icon: CreditCard, label: 'Pay Rent', path: `${tenantBasePath}/payment` },
    { icon: Calendar, label: 'Autopay', path: `${tenantBasePath}/autopay` },
    { icon: Wrench, label: 'Work Orders', path: `${tenantBasePath}/maintenance` },
    { icon: FileText, label: 'Documents', path: `${tenantBasePath}/documents` },
    { icon: Settings, label: 'Profile', path: `${tenantBasePath}/profile` }
  ];

  const menuItems = userRole === 'tenant' ? tenantMenuItems : landlordMenuItems;

  return (
    <div className="h-screen w-64 bg-white border-r border-gray-200 flex flex-col">
      {/* Mobile Close Button */}
      <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-200">
        <Link to={userRole === 'tenant' ? '/tenant' : '/owner/dashboard'} className="flex items-center space-x-2" onClick={onClose}>
          <Building2 className="h-7 w-7 sm:h-8 sm:w-8 text-blue-600" />
          <div className="flex flex-col">
            <span className="text-base sm:text-lg font-bold text-gray-900">Legacy Property</span>
            <span className="text-xs text-gray-600 -mt-1">Association</span>
          </div>
        </Link>
        {/* Close button for mobile */}
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={onClose}
        >
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 lg:p-6 space-y-1 overflow-y-auto">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path || 
            (item.path !== '/' && location.pathname.startsWith(item.path));
          
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onClose} // Close mobile menu on navigation
              className={cn(
                "flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-colors duration-200 touch-manipulation",
                item.isAdmin && "border-t border-gray-200 mt-2 pt-4", // Separator for admin link
                isActive
                  ? item.isAdmin 
                    ? "bg-red-50 text-red-700 border-r-2 border-red-700" 
                    : "bg-blue-50 text-blue-700 border-r-2 border-blue-700"
                  : item.isAdmin
                    ? "text-red-700 hover:bg-red-50 hover:text-red-800"
                    : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
              )}
            >
              <Icon className="mr-3 h-5 w-5 sm:h-6 sm:w-6" />
              <span className="truncate">{item.label}</span>
              {item.isAdmin && (
                <span className="ml-auto text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                  Admin
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="p-4 lg:p-6 border-t border-gray-200">
        <button 
          onClick={async () => {
            try {
              await logout();
              window.location.href = '/';
            } catch (error) {
              console.error('Logout error:', error);
              window.location.href = '/';
            }
          }}
          className="flex items-center w-full px-3 py-3 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200 touch-manipulation"
        >
          <LogOut className="mr-3 h-5 w-5" />
          Sign Out
        </button>
      </div>
    </div>
  );
};

export default Sidebar;